import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  ArrowDownAZ,
  ArrowDownZA,
  EllipsisVertical,
  Inbox,
  Layers,
  Loader2,
  Palette,
  PanelLeftClose,
  Pencil,
  Plus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Skeleton,
  useToast,
} from '@/components/ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import { useInboxCounts } from '@/hooks/useInboxCounts';
import { useAllLabels, useCreateLabel, useUpdateLabel } from '@/hooks/useLabels';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { useAuth } from '@/store/auth-context';
import { LABEL_COLOR_SWATCHES } from '@/components/labels/colors';
import type { Label } from '@/types/label';
import type { SpaceSummary } from '@/types/spaces';

const COLLAPSE_KEY = 'inbox-rail-collapsed';

const COLOR_SWATCHES = LABEL_COLOR_SWATCHES;

type LabelSort = 'az' | 'za' | 'color';

// Color first: groups same-hued labels, with name as the within-color
// tiebreaker (so it reads as "by color, then A → Z").
const DEFAULT_SORT: LabelSort = 'color';

const SORT_LABELS: Record<LabelSort, string> = {
  az: 'Name A → Z',
  za: 'Name Z → A',
  color: 'Color',
};

function sortLabels(labels: Label[], mode: LabelSort): Label[] {
  const byName = (a: Label, b: Label) => a.name.localeCompare(b.name);
  if (mode === 'az') return [...labels].sort(byName);
  if (mode === 'za') return [...labels].sort((a, b) => byName(b, a));
  // Color: group by swatch order so same-hued labels cluster, unknown
  // colors sink to the end, then fall back to name within each color.
  const rank = (c?: string) => {
    const i = c ? COLOR_SWATCHES.indexOf(c) : -1;
    return i === -1 ? COLOR_SWATCHES.length : i;
  };
  return [...labels].sort((a, b) => {
    const r = rank(a.color) - rank(b.color);
    return r !== 0 ? r : byName(a, b);
  });
}

/**
 * Secondary in-page rail rendered to the left of the inbox content,
 * Gmail-style. Lists every space's labels with swatch + name + a
 * pending count served from the backend `/me/inbox/counts` cache.
 *
 * Lives inside `OperatorInbox` so it only shows on the inbox surface;
 * the global operator sidebar stays a clean icon rail.
 */
export function InboxLabelRail() {
  const [searchParams] = useSearchParams();
  // The page tracks a single `?selected=` (lbl-… or spc-…) — rail rows
  // compare their own id against it for active state. A label selection
  // also activates its space heading (rendered as muted-on-active).
  const selectedParam = searchParams.get('selected');

  const { getCount, totalAll } = useInboxCounts();
  const { bySpace, byId, isLoading: labelsLoading } = useAllLabels();
  const { data: spacesData, isLoading: spacesLoading } = useMeSpaces();

  const [collapsed, setCollapsedState] = useState<boolean>(
    () => typeof window !== 'undefined' && localStorage.getItem(COLLAPSE_KEY) === 'true',
  );
  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, String(next));
    } catch {
      // localStorage can throw in private modes — non-fatal.
    }
  }, []);

  // Sort + hidden-space settings live in the user's preferences so they
  // follow the account across devices. Local state mirrors them for an
  // instant response; writes go through fire-and-forget and re-sync if the
  // server (or another tab) reports a different value.
  const { user, updatePreferences } = useAuth();
  const prefSort = user?.preferences?.inboxLabelSort ?? DEFAULT_SORT;
  const prefHiddenKey = (user?.preferences?.inboxHiddenSpaceIds ?? []).join(',');

  const [sort, setSortState] = useState<LabelSort>(prefSort);
  const [hiddenSpaceIds, setHiddenState] = useState<string[]>(() =>
    prefHiddenKey ? prefHiddenKey.split(',') : [],
  );
  useEffect(() => setSortState(prefSort), [prefSort]);
  useEffect(
    () => setHiddenState(prefHiddenKey ? prefHiddenKey.split(',') : []),
    [prefHiddenKey],
  );

  const setSort = useCallback(
    (next: LabelSort) => {
      setSortState(next);
      updatePreferences({ inboxLabelSort: next }).catch(() => {});
    },
    [updatePreferences],
  );

  const hiddenSpaces = useMemo(() => new Set(hiddenSpaceIds), [hiddenSpaceIds]);
  const toggleSpace = useCallback(
    (spaceId: string) => {
      const next = new Set(hiddenSpaces);
      if (next.has(spaceId)) next.delete(spaceId);
      else next.add(spaceId);
      const arr = [...next];
      setHiddenState(arr);
      updatePreferences({ inboxHiddenSpaceIds: arr }).catch(() => {});
    },
    [hiddenSpaces, updatePreferences],
  );

  const spaces = useMemo(() => {
    const list = spacesData?.items ?? [];
    return [...list].sort((a, b) => {
      const personalRank = (s: { kind: string }) => (s.kind === 'personal' ? 0 : 1);
      const r = personalRank(a) - personalRank(b);
      if (r !== 0) return r;
      return a.name.localeCompare(b.name);
    });
  }, [spacesData]);

  // Only spaces that actually contribute labels to the rail are worth
  // toggling — a label-less space has nothing to show or hide.
  const spacesWithLabels = useMemo(
    () => spaces.filter((s) => (bySpace.get(s.spaceId)?.length ?? 0) > 0),
    [spaces, bySpace],
  );

  const pendingTotal = totalAll;

  // pt-14 (3.5rem ≈ 56px) skips the bulk-action bar's reserved height
  // (h-[3.25rem] = 52px + space-y-1) so the rail's first row sits at
  // the same Y as the first inbox item rather than the bar above it.
  if (collapsed) {
    const activeLabelObj =
      selectedParam && selectedParam.startsWith('lbl-')
        ? byId.get(selectedParam)
        : undefined;
    const activeSpaceObj =
      selectedParam && selectedParam.startsWith('spc-')
        ? spacesData?.items.find((s) => s.spaceId === selectedParam)
        : undefined;
    const tooltipName = activeLabelObj?.name ?? activeSpaceObj?.name ?? 'Inbox';
    const hasScope = !!(activeLabelObj || activeSpaceObj);
    return (
      <aside className="hidden w-10 shrink-0 self-start pt-14 lg:block">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label={`Expand inbox labels (currently: ${tooltipName})`}
              className={cn(
                // Always primary-tinted — we're on the inbox surface, so
                // the icon reads as "active". A bg accent layers in when
                // a filter (label or space) is also active.
                'focus-ring flex h-9 w-9 items-center justify-center rounded-md text-primary transition-colors hover:bg-accent',
                hasScope && 'bg-accent',
              )}
            >
              <Inbox className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{tooltipName}</p>
            {pendingTotal > 0 && !hasScope && (
              <p className="text-xs text-muted-foreground">{pendingTotal} pending</p>
            )}
          </TooltipContent>
        </Tooltip>
      </aside>
    );
  }

  return (
    <aside className="hidden w-56 shrink-0 self-start pt-14 lg:block">
      <nav className="space-y-3">
        {/* Collapse button overlays the count slot on hover — same Y as
            the pending number — so the row stays quiet at rest. */}
        <div className="group relative">
          <NavLink
            to={routes.operator.inbox}
            end
            className={cn(
              'focus-ring flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              !selectedParam
                ? 'bg-accent text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            )}
          >
            <Inbox className="h-4 w-4 shrink-0" />
            <span className="flex-1">All items</span>
            {pendingTotal > 0 && (
              <span className="ml-auto pr-1 text-xs text-muted-foreground transition-opacity group-hover:opacity-0">
                {pendingTotal}
              </span>
            )}
          </NavLink>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse inbox labels"
            title="Collapse"
            className="focus-ring absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100 focus:opacity-100"
          >
            <PanelLeftClose className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="space-y-2">
          {/* pr-2 lines the + button's right edge up with the per-label
              3-dot menus (`right-2` on each row), so the right column
              reads as one vertical line. */}
          <div className="flex items-center justify-between gap-1 pl-3 pr-2 pt-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
              Labels
            </span>
            <LabelRailMenu
              sort={sort}
              onSort={setSort}
              allSpaces={spaces}
              spaces={spacesWithLabels}
              hiddenSpaces={hiddenSpaces}
              onToggleSpace={toggleSpace}
            />
          </div>

          {spacesLoading || labelsLoading
            ? // Skeleton placeholders so the rail has the same vertical
              // shape during initial load — caller can't tell whether the
              // missing rows are real-empty or still fetching.
              Array.from({ length: 2 }).map((_, i) => (
                <LabelSectionSkeleton key={i} />
              ))
            : null}
          {spaces.map((space) => {
            const labels = bySpace.get(space.spaceId) ?? [];
            if (labels.length === 0) return null;
            if (hiddenSpaces.has(space.spaceId)) return null;
            return (
              <LabelSection
                key={space.spaceId}
                spaceId={space.spaceId}
                spaceName={space.name}
                labels={sortLabels(labels, sort)}
                getCount={getCount}
                selectedParam={selectedParam}
              />
            );
          })}
        </div>
      </nav>
    </aside>
  );
}

function LabelSection({
  spaceId,
  spaceName,
  labels,
  getCount,
  selectedParam,
}: {
  spaceId: string;
  spaceName: string;
  labels: Label[];
  getCount: (spaceId: string, labelId: string) => number;
  selectedParam: string | null;
}) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  // Heading reads "active" when the URL selection equals this space id.
  // A label selection inside the same space leaves the heading muted.
  const spaceHeadingActive = selectedParam === spaceId;

  return (
    <div className="space-y-0.5">
      <NavLink
        to={`${routes.operator.inbox}?selected=${spaceId}`}
        className={cn(
          'focus-ring block rounded-md px-3 pt-1 text-[10px] font-semibold uppercase tracking-wide transition-colors',
          spaceHeadingActive
            ? 'text-primary'
            : 'text-muted-foreground/60 hover:text-foreground',
        )}
      >
        {spaceName}
      </NavLink>
      <ul className="space-y-0.5">
        {labels.map((label) => {
          const count = getCount(spaceId, label.labelId);
          if (renamingId === label.labelId) {
            return (
              <li key={label.labelId}>
                <RenameLabelInput
                  label={label}
                  onDone={() => setRenamingId(null)}
                />
              </li>
            );
          }
          return (
            <li key={label.labelId} className="group/label relative">
              <NavLink
                to={`${routes.operator.inbox}?selected=${label.labelId}`}
                className={cn(
                  'focus-ring flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  selectedParam === label.labelId
                    ? 'bg-accent text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: label.color || 'currentColor' }}
                  aria-hidden
                />
                <span className="flex-1 truncate">{label.name}</span>
                {count > 0 ? (
                  <span className="ml-auto pr-1 text-[11px] text-muted-foreground transition-opacity group-hover/label:opacity-0">
                    {count}
                  </span>
                ) : null}
              </NavLink>
              <LabelRowMenu
                label={label}
                onStartRename={() => setRenamingId(label.labelId)}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function LabelRowMenu({
  label,
  onStartRename,
}: {
  label: Label;
  onStartRename: () => void;
}) {
  const update = useUpdateLabel();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Label options"
          title="Label options"
          className="focus-ring absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/label:opacity-100 focus:opacity-100 data-[state=open]:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <EllipsisVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="right"
        sideOffset={4}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onStartRename();
          }}
        >
          <Pencil className="mr-2 h-3.5 w-3.5" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Palette className="mr-2 h-3.5 w-3.5" />
            Label color
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="p-2">
              <div className="grid grid-cols-4 gap-1.5">
                {COLOR_SWATCHES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Set color ${c}`}
                    onClick={() =>
                      update.mutate({
                        spaceId: label.spaceId,
                        labelId: label.labelId,
                        color: c,
                      })
                    }
                    className={cn(
                      'focus-ring h-5 w-5 rounded-full border-2 transition-transform',
                      label.color === c
                        ? 'border-foreground scale-110'
                        : 'border-transparent hover:scale-105',
                    )}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Rail-level controls, modelled on the AUI table column menu: create a
// label, choose a sort, and toggle which spaces' labels show. The create
// form opens as a popover anchored to the same ⋮ button (the trigger is
// both the DropdownMenu trigger and the Popover anchor). Sort + hidden
// spaces persist to localStorage so the rail comes back the way you left it.
function LabelRailMenu({
  sort,
  onSort,
  allSpaces,
  spaces,
  hiddenSpaces,
  onToggleSpace,
}: {
  sort: LabelSort;
  onSort: (next: LabelSort) => void;
  allSpaces: SpaceSummary[];
  spaces: SpaceSummary[];
  hiddenSpaces: Set<string>;
  onToggleSpace: (spaceId: string) => void;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  return (
    <Popover open={createOpen} onOpenChange={setCreateOpen}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <PopoverAnchor asChild>
            <button
              type="button"
              aria-label="Label list options"
              title="Add, sort, and filter labels"
              className="focus-ring rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground"
            >
              <EllipsisVertical className="h-3.5 w-3.5" />
            </button>
          </PopoverAnchor>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          className="w-52"
          // Don't pull focus back to the trigger on close — the create
          // popover's own autoFocus should win when "New label" is chosen.
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
            New label
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] font-normal uppercase tracking-wide text-muted-foreground">
            Sort labels
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={sort}
            onValueChange={(v) => onSort(v as LabelSort)}
          >
            <DropdownMenuRadioItem value="az">
              <span className="flex-1">{SORT_LABELS.az}</span>
              <ArrowDownAZ className="ml-2 h-4 w-4 text-muted-foreground" />
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="za">
              <span className="flex-1">{SORT_LABELS.za}</span>
              <ArrowDownZA className="ml-2 h-4 w-4 text-muted-foreground" />
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="color">
              <span className="flex-1">{SORT_LABELS.color}</span>
              <Palette className="ml-2 h-4 w-4 text-muted-foreground" />
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          {spaces.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Layers className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                  Spaces
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-72 w-56 overflow-y-auto">
                    <DropdownMenuLabel className="text-[11px] font-normal uppercase tracking-wide text-muted-foreground">
                      Show labels from
                    </DropdownMenuLabel>
                    {spaces.map((space) => (
                      <DropdownMenuCheckboxItem
                        key={space.spaceId}
                        checked={!hiddenSpaces.has(space.spaceId)}
                        // Keep the submenu open so several spaces can be
                        // toggled in one pass.
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={() => onToggleSpace(space.spaceId)}
                      >
                        <span className="truncate">{space.name}</span>
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="w-64 p-3"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <CreateLabelForm
          spaces={allSpaces}
          onClose={() => setCreateOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function RenameLabelInput({
  label,
  onDone,
}: {
  label: Label;
  onDone: () => void;
}) {
  const update = useUpdateLabel();
  const [value, setValue] = useState(label.name);

  function commit() {
    const next = value.trim();
    if (next && next !== label.name) {
      update.mutate({
        spaceId: label.spaceId,
        labelId: label.labelId,
        name: next,
      });
    }
    onDone();
  }

  return (
    <div className="flex items-center gap-2 rounded-md bg-accent/40 px-3 py-1.5">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: label.color || 'currentColor' }}
        aria-hidden
      />
      <input
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onDone();
          }
        }}
        onBlur={commit}
        className="flex-1 bg-transparent text-xs font-medium text-foreground outline-none"
      />
    </div>
  );
}

function LabelSectionSkeleton() {
  return (
    <div className="space-y-0.5" aria-hidden>
      <Skeleton className="mx-3 mt-1 h-2.5 w-20" />
      <ul className="space-y-0.5">
        {Array.from({ length: 3 }).map((_, i) => (
          <li
            key={i}
            className="flex items-center gap-2 rounded-md px-3 py-1.5"
          >
            <Skeleton className="h-2 w-2 shrink-0 rounded-full" />
            <Skeleton className="h-3 flex-1 max-w-[70%]" />
          </li>
        ))}
      </ul>
    </div>
  );
}

// The label-creation form, rendered inside the rail menu's popover. Owns
// its own draft state; calls onClose after a successful create or cancel.
function CreateLabelForm({
  spaces,
  onClose,
}: {
  spaces: SpaceSummary[];
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateLabel();
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLOR_SWATCHES[0]);
  const [spaceId, setSpaceId] = useState(() => spaces[0]?.spaceId ?? '');

  async function submit() {
    if (!name.trim() || !spaceId) return;
    try {
      await create.mutateAsync({ spaceId, name: name.trim(), color });
      toast.success(`Created label “${name.trim()}”`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create label');
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">
          Space
        </label>
        <select
          value={spaceId}
          onChange={(e) => setSpaceId(e.target.value)}
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
        >
          {spaces.map((s) => (
            <option key={s.spaceId} value={s.spaceId}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">
          Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder="Billing"
          autoFocus
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
        />
      </div>
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-muted-foreground">
          Color
        </label>
        <div className="flex flex-wrap gap-1.5">
          {COLOR_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Color ${c}`}
              onClick={() => setColor(c)}
              className={cn(
                'focus-ring h-5 w-5 rounded-full border-2 transition-transform',
                color === c
                  ? 'border-foreground scale-110'
                  : 'border-transparent hover:scale-105',
              )}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="focus-ring rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          disabled={create.isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!name.trim() || !spaceId || create.isPending}
          className="focus-ring inline-flex items-center gap-1.5 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {create.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          Create
        </button>
      </div>
    </div>
  );
}
